import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import RichTextEditor from "../../../components/base/RichTextEditor";

interface Props {
  lang: "vi" | "kr";
}

interface IntroPage {
  id: string;
  slug: string;
  title_vi: string;
  title_kr: string;
  content_vi: string;
  content_kr: string;
  is_published: boolean;
  updated_at: string;
}

const T = (lang: "vi" | "kr", vi: string, kr: string) => (lang === "vi" ? vi : kr);

const SLUGS = [
  { slug: "loi-chao", labelVi: "Lời chào", labelKr: "인사말" },
  { slug: "khai-quat-cong-ty", labelVi: "Khái quát công ty", labelKr: "회사 개요" },
];

const IntroductionManager = ({ lang }: Props) => {
  const [pages, setPages] = useState<IntroPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState("loi-chao");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState<Partial<IntroPage>>({
    title_vi: "",
    title_kr: "",
    content_vi: "",
    content_kr: "",
    is_published: true,
  });

  const fetchPages = async () => {
    setLoading(true);
    const { data } = await supabase.from("introduction_pages").select("*").order("slug");
    const rows = (data as IntroPage[]) ?? [];
    setPages(rows);
    if (rows.length > 0) {
      const active = rows.find((r) => r.slug === activeSlug) ?? rows[0];
      setActiveSlug(active.slug);
      setForm({
        title_vi: active.title_vi ?? "",
        title_kr: active.title_kr ?? "",
        content_vi: active.content_vi ?? "",
        content_kr: active.content_kr ?? "",
        is_published: active.is_published ?? true,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const selectSlug = (slug: string) => {
    setActiveSlug(slug);
    const active = pages.find((r) => r.slug === slug);
    if (active) {
      setForm({
        title_vi: active.title_vi ?? "",
        title_kr: active.title_kr ?? "",
        content_vi: active.content_vi ?? "",
        content_kr: active.content_kr ?? "",
        is_published: active.is_published ?? true,
      });
    }
    setSaved(false);
    setError("");
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSaved(false);

    const payload = {
      slug: activeSlug,
      title_vi: form.title_vi?.trim() ?? "",
      title_kr: form.title_kr?.trim() ?? "",
      content_vi: form.content_vi ?? "",
      content_kr: form.content_kr ?? "",
      is_published: form.is_published ?? true,
      updated_at: new Date().toISOString(),
    };

    const { error: dbErr } = await supabase
      .from("introduction_pages")
      .upsert(payload, { onConflict: "slug" });

    setSaving(false);
    if (dbErr) {
      setError(dbErr.message);
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    fetchPages();
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white transition-colors";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1.5";

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">
          {T(lang, "Quản lý Giới thiệu", "소개 관리")}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {T(lang, "Cập nhật nội dung trang Lời chào và Khái quát công ty", "인사말 및 회사 개요 페이지 내용 업데이트")}
        </p>
      </div>

      {saved && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-green-600" />
          <span className="text-sm text-green-700 font-medium">
            {T(lang, "Đã lưu! Website cập nhật ngay lập tức.", "저장됨! 웹사이트가 즉시 업데이트됩니다.")}
          </span>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-600" />
          <span className="text-sm text-red-700 font-medium">{error}</span>
        </div>
      )}

      {/* Tab selector */}
      <div className="flex gap-2 mb-6">
        {SLUGS.map((s) => {
          const isActive = activeSlug === s.slug;
          return (
            <button
              key={s.slug}
              onClick={() => selectSlug(s.slug)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border-2 ${
                isActive
                  ? "bg-[#004A99] text-white border-[#004A99]"
                  : "bg-white text-gray-600 border-gray-200 hover:border-[#004A99]/40"
              }`}
            >
              <div className={`w-4 h-4 flex items-center justify-center ${isActive ? "text-white" : "text-gray-400"}`}>
                <i className={s.slug === "loi-chao" ? "ri-hand-heart-line" : "ri-building-2-line"} />
              </div>
              {lang === "vi" ? s.labelVi : s.labelKr}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-16">
          <i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{T(lang, "Tiêu đề (Tiếng Việt)", "제목 (베트남어)")}</label>
              <input
                className={inputCls}
                value={form.title_vi ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, title_vi: e.target.value }))}
                placeholder={T(lang, "Nhập tiêu đề...", "제목을 입력하세요...")}
              />
            </div>
            <div>
              <label className={labelCls}>{T(lang, "Tiêu đề (Tiếng Hàn)", "제목 (한국어)")}</label>
              <input
                className={inputCls}
                value={form.title_kr ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, title_kr: e.target.value }))}
                placeholder={T(lang, "Nhập tiêu đề Hàn...", "한국어 제목...")}
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>{T(lang, "Nội dung (Tiếng Việt)", "내용 (베트남어)")}</label>
            <RichTextEditor
              value={form.content_vi ?? ""}
              onChange={(html) => setForm((p) => ({ ...p, content_vi: html }))}
              placeholder={T(lang, "Nhập nội dung chi tiết tại đây...", "상세 내용을 입력하세요...")}
              minHeight={300}
              folder="introduction"
              label={T(lang, "Nội dung trang", "페이지 내용")}
            />
          </div>

          <div>
            <label className={labelCls}>{T(lang, "Nội dung (Tiếng Hàn)", "내용 (한국어)")}</label>
            <RichTextEditor
              value={form.content_kr ?? ""}
              onChange={(html) => setForm((p) => ({ ...p, content_kr: html }))}
              placeholder={T(lang, "Nhập nội dung Hàn...", "한국어 내용...")}
              minHeight={300}
              folder="introduction"
              label={T(lang, "Nội dung tiếng Hàn", "한국어 내용")}
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_published ?? true}
                onChange={(e) => setForm((p) => ({ ...p, is_published: e.target.checked }))}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-gray-700">
                {T(lang, "Xuất bản ngay", "즉시 게시")}
              </span>
            </label>
          </div>

          <div className="pt-3 border-t border-gray-100 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#004A99] hover:bg-[#003a7a] text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors"
            >
              {saving && <i className="ri-loader-4-line animate-spin" />}
              <i className="ri-save-line" />
              {saving ? T(lang, "Đang lưu...", "저장 중...") : T(lang, "Lưu nội dung", "내용 저장")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntroductionManager;